<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's',
        [$style.mediumScreen]: $screen === 'm'
      }
    ]"
  >
    <div :class="$style.searchHolder">
      <BaseHeading
        size="xl"
        mb
      >
        {{ $t('support.hub.heading') }}
      </BaseHeading>
      <div :class="$style.searchInput">
        <BaseForm @submit="() => search(keyword)">
          <BaseSearch
            v-model="keyword"
            :placeholder="$t('support.hub.search.placeholder')"
          />
        </BaseForm>
      </div>
    </div>
    <div :class="$style.main">
      <BaseHeading mb>{{ $t('support.hub.topics.heading') }}</BaseHeading>
      <div :class="$style.categories">
        <BaseCard
          v-for="topic in supportTopics"
          :key="topic.icon"
          gray
          fullWidth
          noPadding
        >
          <div
            :class="$style.topic"
            :style="theme === 'DARK' ? { 'background-image': `linear-gradient(90deg,rgba(0, 0, 0, .5) 0%, rgba(0, 0, 0, .5) 100%), url(${topic.imageUrl})` } : { 'background-image': `linear-gradient(90deg,rgba(255, 255, 255, .5) 0%, rgba(255, 255, 255, .5) 100%), url(${topic.imageUrl})` }"
            @click="open(topic.query)"
          >
            <BaseIcon
              :name="topic.icon"
              size="l"
              :mb="0.5"
            />
            <BaseHeading>{{ topic.label }}</BaseHeading>
          </div>
        </BaseCard>
      </div>
      <div :class="$style.actions">
        <BaseCard
          :heading="$t('support.hub.idea_box.heading')"
          gray
        >
          {{ $t('support.hub.idea_box.description') }}
          <BaseButton
            color="inverted"
            fullWidth
            mt
            @click="
              open(
                'https://portal.productboard.com/salonized/1-salonized-ideas-box/tabs/4-ideas'
              )
            "
          >
            {{ $t('support.hub.idea_box.button') }}
          </BaseButton>
        </BaseCard>
        <BaseCard
          v-if="unleash.isEnabled('feedbackWidget')"
          :heading="$t('support.hub.feedback.heading')"
          gray
        >
          {{ $t('support.hub.feedback.description') }}
          <BaseButton
            color="inverted"
            fullWidth
            mt
            v-test="'report-bug-feedback'"
            @click="reportBugOrFeedback"
          >
            {{ $t('support.hub.feedback.button') }}
          </BaseButton>
        </BaseCard>
      </div>
      <div :class="$style.popularArticlesWrap">
        <BaseHeading :mb=".5">
          {{ $t('support.hub.popular_articles.heading') }}
        </BaseHeading>
        <div :class="$style.popularArticles">
          <div
            v-for="(article, index) in popularArticles"
            :key="`article_${index}`"
            :class="$style.article"
            @click="open(article.link)"
          >
            <BaseText>{{ article.heading }}</BaseText>
            <BaseIcon name="arrow-right" />
          </div>
        </div>
      </div>
      <BaseText
        :mt="2"
        :routerLink="{ name: 'admin' }"
      >
        {{ $t('support.hub.banner') }}
      </BaseText>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { IconName } from '@/components/base-icon/types';
import {
  helpCenterArticleUrl,
  helpCenterCollectionUrl,
  helpCenterSearchUrl,
} from '@/helpers/help-center';
import productFruitsService from '@/product-fruits';
import { useThemeStore } from '@/stores/theme';
import unleash from '@/unleash';

const { theme } = useThemeStore();

const reportBugOrFeedback = () => productFruitsService.showFeedbackWidget();

const { t } = useI18n();

const keyword = ref('');

const search = (keyword: string) =>
  window.open(helpCenterSearchUrl(keyword), '_blank');
const open = (link: string) => window.open(link, '_blank');

const popularArticles = [
  {
    heading: t('support.hub.popular_articles.article_1.heading'),
    link: helpCenterArticleUrl(
      t('support.hub.popular_articles.article_1.link'),
    ),
  },
  {
    heading: t('support.hub.popular_articles.article_2.heading'),
    link: helpCenterArticleUrl(
      t('support.hub.popular_articles.article_2.link'),
    ),
  },
  {
    heading: t('support.hub.popular_articles.article_3.heading'),
    link: helpCenterArticleUrl(
      t('support.hub.popular_articles.article_3.link'),
    ),
  },
  {
    heading: t('support.hub.popular_articles.article_4.heading'),
    link: helpCenterArticleUrl(
      t('support.hub.popular_articles.article_4.link'),
    ),
  },
  {
    heading: t('support.hub.popular_articles.article_5.heading'),
    link: helpCenterArticleUrl(
      t('support.hub.popular_articles.article_5.link'),
    ),
  },
  {
    heading: t('support.hub.popular_articles.article_6.heading'),
    link: helpCenterArticleUrl(
      t('support.hub.popular_articles.article_6.link'),
    ),
  },
];

const supportTopics: {
  icon: IconName;
  label: string;
  query: string;
  imageUrl: string;
}[] = [
  {
    icon: 'calendar',
    label: t('support.hub.topics.calendar.title'),
    query: helpCenterCollectionUrl(t('support.hub.topics.calendar.query')),
    imageUrl: '/img/support/calendar.png',
  },
  {
    icon: 'people',
    label: t('support.hub.topics.customers.title'),
    query: helpCenterCollectionUrl(t('support.hub.topics.customers.query')),
    imageUrl: '/img/support/customers.png',
  },
  {
    icon: 'shopping-cart',
    label: t('support.hub.topics.register.title'),
    query: helpCenterArticleUrl(t('support.hub.topics.register.query')),
    imageUrl: '/img/support/register.png',
  },
  {
    icon: 'treatwell',
    label: t('support.hub.topics.treatwell.title'),
    query: helpCenterArticleUrl(t('support.hub.topics.treatwell.query')),
    imageUrl: '/img/support/treatwell.png',
  },
];
</script>

<style
  lang="scss"
  module
>
.searchHolder {
  background-color: var(--color-highlight);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $spacing * 3 $spacing;
  border-bottom: 1px solid var(--border);
}
.searchInput {
  position: relative;
  max-width: 100%;
  width: 600px;
}
.searchLabels {
  display: flex;
  margin-top: $spacing;

  & > *:not(:last-child) {
    margin-right: $spacing * 0.5;
  }
}
.categories {
  .base:not(.smallScreen):not(.mediumScreen) & {
    display: flex;
    align-items: center;
    justify-content: space-between;

    & > *:not(:last-child) {
      margin-right: $spacing;
    }
  }

  .base.smallScreen &,
  .base.mediumScreen & {
    & > *:not(:last-child) {
      margin-bottom: $spacing;
    }
  }
}

.topic {
  cursor: pointer;
  padding: $spacing * 3 $spacing $spacing;
  height: 130px;
  background-size: 100% 100%, 135px auto;
  background-repeat: no-repeat;
  background-position: bottom right;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-items: flex-end;
}

.main {
  padding: 0 $spacing;
  margin: $spacing * 2 auto 0;
  max-width: 1000px;
}

.actions {
  margin-top: $spacing;

  .base:not(.smallScreen):not(.mediumScreen) & {
    display: flex;
    justify-content: space-between;

    & > * {
      width: calc(50% - #{$spacing * 0.5});
    }

    & > *:only-child {
      width: 600px;
      max-width: 100%;
      margin: 0 auto;
    }
  }

  .base.smallScreen &,
  .base.mediumScreen & {
    & > *:not(:last-child) {
      margin-bottom: $spacing;
    }
  }
}

.popularArticlesWrap {
  margin-top: $spacing;
  padding: $spacing;
}

.popularArticles {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin: 0 $spacing * -0.5;
}

.article {
  width: calc(50% - $spacing);
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  padding: $spacing * 0.5;
  margin-right: $spacing * 0.5;

  @include hover {
    background-color: var(--color-highlight);
  }
}
</style>
